import { useQueries } from "@tanstack/react-query";
import { useMemo } from "react";
import type { Environment } from "../../../types";
import type { TokenBalance, TokenBalanceResponse } from "../types";
import { CHAIN_IDS } from "../constants";
import { getApiBase } from "../../../lib/remote-config";

export const USER_BALANCE_QUERY_KEY = "user-balance";

async function fetchBalance(
  baseUrl: string,
  chainId: number,
  walletAddress: string,
  signal?: AbortSignal,
): Promise<TokenBalance[]> {
  const params = new URLSearchParams({
    networkId: `eip155:${chainId}`,
    account: walletAddress,
  });
  const res = await fetch(`${baseUrl}/v1/public/token/balance?${params}`, {
    cache: "no-store",
    signal,
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch token balance: ${res.status}`);
  }
  const json: TokenBalanceResponse = await res.json();
  if (json.code !== 200) {
    throw new Error(`Token balance API error: ${json.code}`);
  }
  return json.data;
}

export function useTokenBalance(
  env: Environment,
  walletAddress: string,
  active = true,
) {
  const chains = CHAIN_IDS[env];
  const chainIds = [chains.cross, chains.bsc];

  const results = useQueries({
    queries: chainIds.map((chainId) => ({
      queryKey: [USER_BALANCE_QUERY_KEY, env, walletAddress, chainId],
      queryFn: async ({ signal }: { signal?: AbortSignal }) =>
        fetchBalance(
          await getApiBase("walletServer", env),
          chainId,
          walletAddress,
          signal,
        ),
      enabled: !!walletAddress && active,
      staleTime: 0,
      refetchInterval: active ? 5000 : false,
    })),
  });

  const isLoading = results.some((r) => r.isLoading);
  const isError = results.some((r) => r.isError);

  const tokens = useMemo(() => {
    const merged: TokenBalance[] = [];
    for (const r of results) {
      if (r.data) merged.push(...r.data);
    }
    return merged;
  }, [results]);

  return { tokens, isLoading, isError };
}
